import { KITS, KIT_IDS } from './kits';

/**
 * Which three kits to show, and why they must not resemble each other.
 *
 * The wizard offers three looks and invites the person to pick one. A model asked for "the
 * three best looks for a staff rota" will answer with three defensible sober sans-serif kits,
 * because they ARE all defensible — and the person is then given three near-identical cards
 * and a decision that does not feel like one. The point of the step is to show that their app
 * could go several genuinely different ways.
 *
 * So the ranking decides ORDER and this decides SPREAD: one kit per family, top pick always
 * kept. Variety stops being a hope about the reply and becomes a property of the code.
 */

/** The look each kit belongs to. Two kits in one family are alternatives, not options. */
export const KIT_FAMILY: Record<string, string> = {
  ledger: 'precise',
  console: 'precise',
  editorial: 'literary',
  notebook: 'literary',
  sport: 'bold',
  arcade: 'bold',
  soft: 'warm',
  gallery: 'quiet',
};

export const OPTION_COUNT = 3;

/**
 * Three kit ids: ranked best-first, one per family, always full.
 *
 * `round` is "show me others" — each tap offers three the person was not just shown. Eight
 * kits cannot yield unlimited fresh triples, so it cycles rather than running dry: somebody
 * who keeps tapping sees earlier kits come back round instead of watching the options thin
 * out and stop.
 */
export function kitOptions(ranked: readonly string[] | undefined, round = 0): string[] {
  /*
   * Tolerates a missing ranking, not just an empty one.
   *
   * The wizard restores an AppPlan from MMKV, so a plan saved before `kitRanking` existed
   * arrives with the field simply absent — which is every plan in progress on every phone the
   * day this ships. Taking `undefined` here rather than asking each caller to remember a
   * fallback keeps the absent case the safe case, which is where this codebase has been bitten
   * four times already (webBookable, ownership, listed, kind).
   */
  const valid = (ranked ?? []).filter((id) => KIT_IDS.includes(id));
  // Everything not named by the ranking is still eligible — a ranking of one must still
  // produce three, and a ranking of none must still produce a choice.
  const rest = KIT_IDS.filter((id) => !valid.includes(id));
  const pool = [...valid, ...rest];

  /*
   * Each round draws from the kits shown LONGEST AGO: never shown first, in ranking order,
   * then the ones last shown earliest.
   *
   * This used to rotate the ranking by one place per round, so a tap on "Show me three other
   * looks" usually swapped one card and left two where they were — ledger, editorial, sport
   * became editorial, sport, soft. Sorting the three just on screen to the back makes the next
   * three all new: every two-kit family still has a member off screen, so the kits not just
   * shown always span three families (the test holds today's families to that). Round 0 is
   * untouched — nothing has been shown, so it is the ranking's own spread, top pick first.
   */
  const lastShown = new Map<string, number>();
  const seen = (id: string) => lastShown.get(id) ?? -1;
  let picked = spread(pool);
  for (let r = 1; r <= round; r += 1) {
    for (const id of picked) lastShown.set(id, r - 1);
    // Ties keep ranking order explicitly rather than trusting the engine's sort stability.
    const order = [...pool].sort((a, b) => seen(a) - seen(b) || pool.indexOf(a) - pool.indexOf(b));
    picked = spread(order);
  }
  return picked;
}

/** The first three of `order` from different families, filled to three if families run out. */
function spread(order: readonly string[]): string[] {
  const picked: string[] = [];
  const families = new Set<string>();
  for (const id of order) {
    if (picked.length === OPTION_COUNT) break;
    const family = KIT_FAMILY[id];
    if (families.has(family)) continue;
    picked.push(id);
    families.add(family);
  }
  // A family set smaller than the slot count would leave this short; fill from the order
  // so the step always renders three cards rather than a ragged row.
  for (const id of order) {
    if (picked.length === OPTION_COUNT) break;
    if (!picked.includes(id)) picked.push(id);
  }
  return picked;
}

/**
 * Rounds within which every kit has been offered at least once. Not "before the offer repeats":
 * drawing the least recently shown makes the cycle depend on the ranking. Four is enough for
 * every order of today's eight kits; the test checks all of them against this bound.
 */
export const ROUNDS = KITS.length;

/**
 * The ranking with `kitId` moved to the top — for a look the person chose some other way.
 *
 * The wizard resets the selected kit to the first one on offer whenever the selection is not
 * among the three shown, so a build never uses a look nobody can see. A kit matched from a
 * picture is usually NOT among them: set on its own, it was undone on the very next render
 * while the note under the chips still said "Matched to …". Putting it first in the ranking
 * makes it the top pick, which `kitOptions` always keeps.
 */
export function withTopPick(ranked: readonly string[] | undefined, kitId: string): string[] {
  return [kitId, ...(ranked ?? []).filter((id) => id !== kitId)];
}
